// Check-in step description merge-field renderer.
//
// Hosts author check-in step text in the admin dashboard using
// {{placeholder}} tokens. The admin previews them with real values
// substituted in; the guest portal must do the same so the guest
// never sees a raw `{{code}}` token.
//
// This file is a 1:1 port of the admin renderer — DO NOT diverge
// the regex, the lookup order, or the unresolved-tracking behavior
// without coordinating both sides, or admin previews will drift
// from what the guest actually sees. If admin + guest portals ever
// share an npm package, this is the file to extract.

// Supported syntax (mirrors admin):
//   {{key}}                → context[key], else ""
//   {{key|fallback text}}  → context[key], else "fallback text"
//   {{ key }}              → whitespace inside the braces is OK
//
// The capture group `[^}|\s]+` for the key explicitly rejects pipes
// (which delimit the fallback) and whitespace, so it doesn't gobble
// the fallback text.
const PLACEHOLDER_RE = /\{\{\s*([^}|\s]+)\s*(?:\|([^}]*))?\}\}/g;

// `options.wrap` is an optional presentation hook for callers that
// need to visually distinguish substituted segments (e.g. the guest
// portal bolds them by wrapping in <strong>). Signature:
//   wrap(text, { key, isFallback }) => string
// Default is identity, so callers that don't pass it (including
// admin) get the exact same plain-string output as before — the
// 1:1 semantics with the admin renderer are preserved.
export function renderCheckInTemplate(text, context = {}, options = {}) {
  if (!text) return { rendered: "", unresolved: [] };
  const wrap =
    typeof options.wrap === "function" ? options.wrap : (value) => value;
  const unresolved = [];
  const rendered = text.replace(
    PLACEHOLDER_RE,
    (_match, rawKey, rawFallback) => {
      const key = (rawKey || "").trim();
      const fallback = rawFallback != null ? rawFallback : null;

      // Try the flat key first (e.g. `context["guest.firstName"]`),
      // then fall back to walking the dotted path
      // (`context.guest.firstName`). Admin uses both shapes; the
      // flat-first order matches the admin renderer so a context
      // that defines BOTH resolves identically on both sides.
      let value = context[key];
      if (value == null) {
        value = key
          .split(".")
          .reduce((cur, part) => (cur == null ? cur : cur[part]), context);
      }

      if (value !== null && value !== undefined && value !== "") {
        return wrap(String(value), { key, isFallback: false });
      }

      unresolved.push(key);
      // Empty resolution (no value, no fallback) stays empty — never
      // wrap an empty string, otherwise we'd emit a stray `<strong></strong>`.
      if (fallback == null) return "";
      return wrap(fallback, { key, isFallback: true });
    }
  );
  return { rendered, unresolved };
}

// Builds the guest-portal merge context from the data already on the
// check-in screen. Only the 5 admin-supported keys are populated; any
// other placeholder the host wrote will resolve to its fallback (or
// empty string if no fallback). Keys MUST match the admin's
// CHECK_IN_FIELDS list exactly — see admin's merge-field picker.
export function buildCheckInContext({ booking, guest, room } = {}) {
  const phone = guest?.phone || guest?.phoneNumber || "";
  const phoneLast4 = phone.length >= 4 ? phone.slice(-4) : null;
  return {
    code: booking?.code ?? room?.code ?? null,
    "guest.phoneLast4": phoneLast4,
    // checkInTime / checkOutTime ship pre-formatted from the
    // room record (e.g. "16:00"). The display layer reformats
    // to 12-hour elsewhere; here we pass through verbatim so the
    // rendered string matches whatever the host previewed against.
    checkInTime: room?.checkInTime ?? null,
    checkOutTime: room?.checkOutTime ?? null,
    "guest.firstName":
      guest?.firstName ?? guest?.name?.split(" ")[0] ?? null,
  };
}
